import { useCallback, useEffect, useRef, useState } from "react";
import type { PlayerAIScope } from "@playbookai/player-ai-client";
import {
  PLAYER_AI_SEND_RESULT_EVENT,
  type PlayerAIOpenDetail,
  type PlayerAISendResultDetail,
} from "@playbookai/types";

/**
 * How long a send request may wait to be posted, counted from the click.
 *
 * Enough for the chat to load and a running answer to finish. A card waiting
 * for approval can wait 20 minutes, which is why a private chat with one says
 * so at once instead of running this out.
 */
export const PLAYER_AI_SEND_TIMEOUT_MS = 60_000;

/**
 * One "post this message for me" request from a feature: `playerai:open` with
 * `send` (today the VSM Kaizen row's Push to Jira).
 *
 * AppLayout owns it, not the panel: `PlayerAIRailWired` is rebuilt every time
 * Player AI opens or moves between rail, popover and fullscreen, so a request
 * held there would be lost on close, or posted again by the next panel. The
 * panel only CLAIMS it, once, right before it posts.
 */
export interface PlayerAISendRequest {
  readonly id: string;
  readonly text: string;
  readonly workspace: NonNullable<PlayerAIOpenDetail["workspace"]>;
  readonly requestedAt: number;
  readonly state: "pending" | "claimed";
}

export interface PlayerAISendRequests {
  /** The request a panel may still post; absent once claimed or answered. */
  readonly pending: PlayerAISendRequest | undefined;
  /** Take a `send` open event. */
  readonly start: (detail: PlayerAIOpenDetail) => void;
  /** `true` exactly once per request; the caller must then post it. */
  readonly claim: (id: string) => boolean;
  /** Answer the feature, once per request, and forget the request. */
  readonly report: (result: PlayerAISendResultDetail) => void;
}

/** Whether the Player AI scope is the chat of `workspace`. */
export function isSendRequestScope(
  scope: PlayerAIScope | null,
  workspace: PlayerAISendRequest["workspace"],
): boolean {
  return (
    scope !== null &&
    "workspaceId" in scope &&
    scope.workspaceType === workspace.type &&
    scope.workspaceId === workspace.id
  );
}

function answer(result: PlayerAISendResultDetail): void {
  window.dispatchEvent(
    new CustomEvent(PLAYER_AI_SEND_RESULT_EVENT, { detail: result }),
  );
}

/**
 * The life of a send request, from the click to its one answer.
 *
 * - Pending until a panel claims it, across closing and reopening Player AI.
 * - Answered `timeout` here, Player AI open or closed, when no panel claimed
 *   it within {@link PLAYER_AI_SEND_TIMEOUT_MS}; `workspace_changed` when the
 *   Player AI scope leaves its workspace first.
 * - Once claimed, only the panel that posted it answers, with the real
 *   outcome. Its callback reaches this hook even after that panel closed.
 */
export function usePlayerAISendRequest(
  scope: PlayerAIScope | null,
): PlayerAISendRequests {
  // Refs are the truth: `claim` must answer synchronously and only once, even
  // when StrictMode runs an effect twice. State only re-renders the panel.
  const requestRef = useRef<PlayerAISendRequest | undefined>(undefined);
  // Claimed requests a newer one replaced: still posting, still owed an answer.
  const replacedClaimsRef = useRef(new Set<string>());
  const [pending, setPending] = useState<PlayerAISendRequest | undefined>(
    undefined,
  );

  const report = useCallback((result: PlayerAISendResultDetail): void => {
    if (requestRef.current?.id === result.requestId) {
      requestRef.current = undefined;
      setPending(undefined);
    } else if (!replacedClaimsRef.current.delete(result.requestId)) {
      // Already answered, or never ours.
      return;
    }
    answer(result);
  }, []);

  const start = useCallback((detail: PlayerAIOpenDetail): void => {
    const { requestId, workspace } = detail;
    if (!requestId) return;
    const text = detail.prompt?.trim();
    if (!text || workspace === undefined) {
      answer({ requestId, ok: false, reason: "send_failed" });
      return;
    }
    const previous = requestRef.current;
    if (previous?.id === requestId) return;
    const request: PlayerAISendRequest = {
      id: requestId,
      text,
      workspace: { type: workspace.type, id: workspace.id },
      requestedAt: Date.now(),
      state: "pending",
    };
    requestRef.current = request;
    setPending(request);
    if (previous?.state === "claimed") {
      replacedClaimsRef.current.add(previous.id);
    } else if (previous !== undefined) {
      // One request waits at a time, and the newer click is the one that
      // counts. The older one is answered now: in another workspace, its
      // person moved on; in the same one, it was not sent in time.
      answer({
        requestId: previous.id,
        ok: false,
        reason:
          previous.workspace.type === workspace.type &&
          previous.workspace.id === workspace.id
            ? "timeout"
            : "workspace_changed",
      });
    }
  }, []);

  const claim = useCallback((id: string): boolean => {
    const current = requestRef.current;
    if (current?.id !== id || current.state !== "pending") return false;
    requestRef.current = { ...current, state: "claimed" };
    setPending(undefined);
    return true;
  }, []);

  useEffect(() => {
    if (pending === undefined) return;
    const timer = window.setTimeout(
      () => {
        const current = requestRef.current;
        if (current?.id === pending.id && current.state === "pending") {
          report({ requestId: pending.id, ok: false, reason: "timeout" });
        }
      },
      Math.max(0, pending.requestedAt + PLAYER_AI_SEND_TIMEOUT_MS - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [pending, report]);

  const inRequestWorkspace =
    pending !== undefined && isSendRequestScope(scope, pending.workspace);
  useEffect(() => {
    if (pending !== undefined && !inRequestWorkspace) {
      report({
        requestId: pending.id,
        ok: false,
        reason: "workspace_changed",
      });
    }
  }, [pending, inRequestWorkspace, report]);

  return { pending, start, claim, report };
}
